"use client";

import type { ChatMessage as ChatMessageType } from "@/hooks/use-recruiter-chat";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ChatMessageProps {
  message: ChatMessageType;
}

// ---------------------------------------------------------------------------
// Simple markdown renderer
// ---------------------------------------------------------------------------

function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split("\n");
  const result: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      result.push(
        <ul key={`list-${result.length}`} style={listStyle}>
          {listItems}
        </ul>
      );
      listItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Bullet list items
    if (/^\s*[-*]\s+/.test(line)) {
      const content = line.replace(/^\s*[-*]\s+/, "");
      listItems.push(<li key={i}>{renderInline(content)}</li>);
      continue;
    }

    flushList();

    // Empty line -> spacing
    if (line.trim() === "") {
      result.push(<br key={`br-${i}`} />);
      continue;
    }

    // Regular line
    result.push(
      <span key={i}>
        {renderInline(line)}
        {i < lines.length - 1 && !lines[i + 1]?.match(/^\s*[-*]\s+/) ? (
          <br />
        ) : null}
      </span>
    );
  }

  flushList();
  return result;
}

/**
 * Render inline markdown: **bold**, *italic*, `code`
 */
function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Regex: **bold**, *italic*, `code`
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // **bold**
      parts.push(
        <strong key={match.index}>{match[2]}</strong>
      );
    } else if (match[3]) {
      // *italic*
      parts.push(
        <em key={match.index}>{match[4]}</em>
      );
    } else if (match[5]) {
      // `code`
      parts.push(
        <code key={match.index} style={codeStyle}>
          {match[6]}
        </code>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === "user";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
      }}
    >
      <div
        style={{
          ...bubbleBase,
          maxWidth: isUser ? "75%" : "88%",
          background: isUser
            ? "color-mix(in srgb, var(--accent-blue) 12%, var(--surface-elevated) 88%)"
            : "var(--surface-elevated)",
          borderRadius: isUser
            ? "16px 16px 4px 16px"
            : "16px 16px 16px 4px",
        }}
      >
        <div style={{ lineHeight: "1.5", whiteSpace: "pre-wrap" }}>
          {renderMarkdown(message.content)}
          {message.isStreaming && (
            <span style={cursorStyle}>|</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const bubbleBase: React.CSSProperties = {
  padding: "0.65rem 0.9rem",
  fontSize: "0.9rem",
  color: "var(--text-primary)",
  wordBreak: "break-word",
};

const codeStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--border) 50%, transparent)",
  borderRadius: "4px",
  padding: "0.1rem 0.35rem",
  fontSize: "0.84rem",
  fontFamily: "monospace",
};

const listStyle: React.CSSProperties = {
  margin: "0.35rem 0",
  paddingLeft: "1.2rem",
};

const cursorStyle: React.CSSProperties = {
  display: "inline-block",
  marginLeft: "1px",
  animation: "typing-pulse 1s ease-in-out infinite",
  color: "var(--text-secondary)",
  fontWeight: 300,
};
